import React, { useState, useEffect } from "react";
import API_BASE_URL from "../../../../../../Constants";
import InfoIcon from "@mui/icons-material/Info";

const TaskTypes = [
  { key: "perform", value: "Perform" },
  { key: "witness", value: "Witness" },
  { key: "both", value: "Both" },
];

const currentYear = new Date().getFullYear(); // Get current year (2025)
const InspYears = [currentYear, currentYear + 1]; // Current year and next year

// Helper function to check if test is Cat1 or Cat5
function isCat1OrCat5Label(label = "") {
  const lower = label.toLowerCase();
  return (
    lower.includes("cat 1") ||
    lower.includes("cat1") ||
    lower.includes("cat 5") ||
    lower.includes("cat5")
  );
}

export default function CombinedInspectionWizard({
  building,
  onClose,
  refreshList,
}) {
  // Building information section
  const [agencyName, setAgencyName] = useState("");
  const [devices, setDevices] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  // Agency tests section
  const [agencyTests, setAgencyTests] = useState([]);
  const [inspectionType, setInspectionType] = useState("all"); // "all" or specific test ID
  const [taskType, setTaskType] = useState("perform"); // Default to "perform"
  const [inspYear, setInspYear] = useState(currentYear.toString()); // Default to current year
  const [approvalDate, setApprovalDate] = useState("");
  const [servicer, setServicer] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch agency name
  useEffect(() => {
    if (!building || !building.agencyID) {
      setAgencyName("Unknown Agency");
      return;
    }

    (async () => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencies/${building.agencyID}`,
          { credentials: "include" }
        );
        if (!r.ok) throw new Error("Failed agency fetch");
        const data = await r.json();
        setAgencyName(data.agencyName || data.name || "Unknown Agency");
      } catch (err) {
        console.error("Error agency fetch:", err);
        setAgencyName("Unknown Agency");
      }
    })();
  }, [building]);

  // Fetch devices and automatically select all of them
  useEffect(() => {
    if (!building) return;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/setup/buildings/devices/${building?.buildingID}`,
          { credentials: "include" }
        );
        if (!r.ok) throw new Error("Devices fetch error");
        const data = await r.json();
        const fetchedDevices = Array.isArray(data) ? data : [];
        setDevices(fetchedDevices);

        // Automatically select all devices
        if (fetchedDevices.length > 0) {
          const allDeviceIds = fetchedDevices.map((dev) =>
            String(dev.deviceId)
          );
          setSelectedDeviceIds(allDeviceIds);
        }
      } catch (err) {
        console.error("Error devices fetch:", err);
      }
    })();
  }, [building]);

  // Fetch agency tests
  useEffect(() => {
    if (!building || !building.agencyID) {
      setAgencyTests([]);
      return;
    }
    (async () => {
      try {
        const url = `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${building.agencyID}`;
        const r = await fetch(url, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP error! ${r.status}`);
        const data = await r.json();
        setAgencyTests(data || []);
      } catch (err) {
        console.error("Error fetching agency tests:", err);
        setAgencyTests([]);
      }
    })();
  }, [building]);

  // Check if we can proceed - need management ID and at least one device
  const canProceed = building?.managementID && selectedDeviceIds.length > 0;

  // Submit and create inspections
  const handleFinish = async () => {
    if (!canProceed) {
      alert(
        "Please ensure you have selected at least one device and the building has a management company!"
      );
      return;
    }

    if (!approvalDate) {
      alert("Please select an approval date!");
      return;
    }

    setIsSubmitting(true);

    try {
      // Determine which tests to create
      const testsToCreate =
        inspectionType === "all"
          ? agencyTests.filter((test) => isCat1OrCat5Label(test.label))
          : agencyTests.filter(
              (test) => test.testId === Number(inspectionType)
            );

      if (testsToCreate.length === 0) {
        alert("No eligible tests to create!");
        setIsSubmitting(false);
        return;
      }

      // Create inspections for each selected test
      const createdIds = [];
      for (const test of testsToCreate) {
        const inspectionData = {
          buildingId: Number(building.buildingID),
          deviceIds: selectedDeviceIds.map(Number),
          managementCompanyId: Number(building.managementID) || 0,
          testId: test.testId,
          testLabel: test.label,
          taskType,
          approvalDate,
          servicer,
          inspYear: parseInt(inspYear, 10) || currentYear,
          status: "Active",
          buildingSiteStored: building.buildingReference || "",
        };

        const postRes = await fetch(`${API_BASE_URL}/api/inspections`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(inspectionData),
        });

        if (!postRes.ok) {
          const errText = await postRes.text();
          throw new Error(`HTTP ${postRes.status} => ${errText}`);
        }

        const createdInspection = await postRes.json();
        createdIds.push(createdInspection.inspectionId);
      }

      // Link inspections if there are 2
      if (createdIds.length === 2) {
        const [firstId, secondId] = createdIds;

        // Link first to second
        let linkPayload1 = { linkedInspectionId: secondId };
        const put1 = await fetch(
          `${API_BASE_URL}/api/inspections/id/${firstId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(linkPayload1),
          }
        );
        if (!put1.ok) {
          const errText = await put1.text();
          console.error("Error linking #1 =>", errText);
        }

        // Link second to first
        let linkPayload2 = { linkedInspectionId: firstId };
        const put2 = await fetch(
          `${API_BASE_URL}/api/inspections/id/${secondId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(linkPayload2),
          }
        );
        if (!put2.ok) {
          const errText = await put2.text();
          console.error("Error linking #2 =>", errText);
        }
      }

      // Create ticket for mobile
      await fetch(`${API_BASE_URL}/api/inspections/create-ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(createdIds),
      });

      alert(`Created ${createdIds.length} inspection(s) successfully!`);
      if (refreshList) refreshList();
      onClose();
    } catch (err) {
      console.error("Error creating or linking inspections:", err);
      alert("Error => " + err.message);
      setIsSubmitting(false);
    }
  };

  // Format date to display in user-friendly format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return "Invalid Date";
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-20 z-[9999]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white w-[800px] max-w-[90%] mx-auto mt-[5vh] rounded-lg shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 bg-gray-100 border-b">
          <h2 className="text-xl font-bold">Inspection Wizard</h2>
          <button
            onClick={onClose}
            className="px-2 py-1 bg-gray-300 rounded hover:bg-gray-400"
          >
            X
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4 overflow-y-auto flex-grow">
          {/* Building Info Section */}
          <div className="border rounded p-4 bg-gray-50">
            <h3 className="font-bold mb-2 text-lg">Building Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <p>
                  <strong>Building Site:</strong>{" "}
                  {building?.buildingReference || "(loading)"}
                </p>
              </div>
              <div>
                <p>
                  <strong>Agency Name:</strong> {agencyName || "(none)"}
                </p>
              </div>
              <p className="text-ellipsis text-nowrap col-span-1 overflow-hidden">
                <strong>Management:</strong>{" "}
                {`${building?.managementCompany?.companyName}` || "(none)"}
              </p>
            </div>

            {/* Devices List (Read-only) */}
            <div className="mt-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">
                  Building Devices{" "}
                  <span className="text-blue-500">({devices.length})</span>
                </h4>
                <div className="text-sm text-blue-600">
                  All devices will be included automatically
                </div>
              </div>

              {devices.length === 0 ? (
                <p className="text-gray-500 italic">
                  No devices found for this building.
                </p>
              ) : (
                <div className="max-h-[150px] overflow-y-auto mt-2 border rounded px-2 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                    {devices.map((dev) => (
                      <div
                        key={dev.deviceId}
                        className="flex items-center px-2 py-1 border-b border-gray-100"
                      >
                        <span className="text-sm">
                          #{dev.deviceId} -{" "}
                          {dev.deviceName || dev.deviceType || "Unknown"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {devices.length === 0 && (
                <p className="text-red-500 text-xs mt-1">
                  There must be at least one device to continue.
                </p>
              )}
            </div>
          </div>

          {/* Test Selection Section */}
          <div className="border rounded p-4">
            <h3 className="font-bold mb-2 text-lg">Inspection Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="mb-2">
                  <label className="block mb-1 font-semibold">
                    Inspection Type
                  </label>
                  <select
                    disabled={!building?.agencyID}
                    value={inspectionType}
                    onChange={(e) => setInspectionType(e.target.value)}
                    className="border p-1 w-full rounded"
                  >
                    <option value="all">All Available Inspections</option>
                    {agencyTests
                      .filter((test) => isCat1OrCat5Label(test.label))
                      .map((t) => (
                        <option key={t.testId} value={t.testId}>
                          {t.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="block mb-1 font-semibold">
                    Inspection Year
                  </label>
                  <select
                    className="border p-1 w-full rounded"
                    value={inspYear}
                    onChange={(e) => setInspYear(e.target.value)}
                  >
                    {InspYears.map((yr) => (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-2">
                  <label className="block mb-1 font-semibold">Task Type</label>
                  <select
                    className="border p-1 w-full rounded"
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                  >
                    {TaskTypes.map((tt) => (
                      <option key={tt.key} value={tt.key}>
                        {tt.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="mb-2">
                  <label className="block mb-1 font-semibold">
                    Approval Date
                  </label>
                  <input
                    type="datetime-local"
                    className="border p-1 w-full rounded"
                    value={approvalDate}
                    onChange={(e) => setApprovalDate(e.target.value)}
                  />
                </div>

                <div className="mb-2">
                  <label className="block mb-1 font-semibold">
                    Servicer (Optional)
                  </label>
                  <input
                    type="text"
                    className="border p-1 w-full rounded"
                    value={servicer}
                    onChange={(e) => setServicer(e.target.value)}
                    placeholder="Enter servicer name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Available Tests Section (Replacing Cart) */}
          <div className="border rounded p-4">
            <h3 className="font-bold mb-2 text-lg flex items-center">
              Available Tests
              <div
                className="text-blue-600 ml-2 flex items-center"
                title="Tests eligible for inspection creation"
              >
                <InfoIcon fontSize="small" />
              </div>
            </h3>

            {agencyTests.length === 0 ? (
              <p className="italic text-gray-500">
                No tests available for this agency.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {agencyTests
                  .filter((test) => isCat1OrCat5Label(test.label))
                  .map((test) => (
                    <div
                      key={test.testId}
                      className={`border p-3 rounded-lg ${
                        inspectionType === "all" ||
                        inspectionType === String(test.testId)
                          ? "bg-blue-50 border-blue-300"
                          : "bg-gray-50"
                      }`}
                    >
                      <div className="font-medium text-lg mb-1">
                        {test.label}
                      </div>
                      <div className="text-sm mb-1">
                        <span className="font-semibold">Frequency:</span>{" "}
                        {test.frequency || "Not specified"}
                      </div>
                      <div className="text-sm text-gray-700">
                        {test.description && test.description.length > 100
                          ? `${test.description.substring(0, 100)}...`
                          : test.description || "No description available"}
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {agencyTests.length > 0 &&
              agencyTests.filter((test) => isCat1OrCat5Label(test.label))
                .length === 0 && (
                <p className="italic text-amber-600">
                  No Cat 1 or Cat 5 tests found for this agency. Only Cat 1 and
                  Cat 5 tests can be created with this wizard.
                </p>
              )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-100 flex justify-end">
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1 bg-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleFinish}
              disabled={!canProceed || isSubmitting || !approvalDate}
              className="px-6 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : "Create Inspections"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
